import { Col, Form, Row, Space } from "antd"
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Legend,
  RadioButtons,
  SelectInput,
  TextInput,
  TimePicker,
  UploadDragger,
  notification,
} from "components/common"

import cx from "clsx"
import { IconText } from "components/custom"
import { DATE_FORMAT, DATE_TIME_FORMAT } from "constants"
import { useLazyGetEventsForCalendarQuery } from "features/calendar/api"
import { useLazyGetLeadDetailsQuery } from "features/leads/api"
import { useLazyGetClassificationQuery, useLazyGetProjectsQuery, useLazyGetTaskTypeQuery } from "features/shared/api"
import { schemaRules } from "features/shared/utils"
import { useAddTaskTsrMutation, useGetTSRCallsQuery } from "features/tsr/apis"
import { t } from "i18next"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import theme from "utils/themeVars"
import styles from "./index.module.less"
import { getAddTaskInitialValues } from "./tsrAddTaskInitialValues"
import tsrAddTaskValidationSchema from "./tsrAddTaskValidation"
import { useUpdateTaskMutation } from "features/tasks/api"
import { configs } from "configs"
import { skipToken } from "@reduxjs/toolkit/query"
import {
  filterSubTaskchildren,
  getAlert,
  getDeadlineRules,
  getSubTasks,
  isSubTaskSalesEvent,
  renderSuggestedComments,
} from "features/tasks/components/addTaskForm/components/utilis"
import { computeDeadline, isSubTaskMeetingPushed, isSubTaskMeetingRescheduled } from "../utilis"

const TsrAddTaskForm = ({ buttonText, lead, client, setFormVisibility, isEditing, setOpen = () => {} }) => {
  const from = { page: PAGES.telesales_listing_page, interacted_from: PAGE_SUBSECTIONS.telesales_listing_page }
  const [getProjects, { data: projects }] = useLazyGetProjectsQuery()
  const [getClassifications, { data: classifications }] = useLazyGetClassificationQuery()
  const [addTask, { isLoading, isError, error, reset: resetErrorForAddTask }] = useAddTaskTsrMutation()
  const [getLeadDetails, { data: leadDetailInfo }] = useLazyGetLeadDetailsQuery()
  const [getTaskTypes, { data: taskTypes }] = useLazyGetTaskTypeQuery()
  const { data: callTypes } = useGetTSRCallsQuery(client?.id ? client?.id : skipToken)
  const [updateTask] = useUpdateTaskMutation()

  const [getSalesEvents, { data: salesEvents, isFetching: isFetchingsSaleEvents }] = useLazyGetEventsForCalendarQuery()

  const [form] = Form.useForm()
  const { setFieldsValue } = form
  const attachment = Form.useWatch("attachment", form)
  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form)
  const subofSubTask = Form.useWatch("subofSubTask", form)
  const dateSlot = Form.useWatch("dateSlot", form)
  const salesEvent = Form.useWatch("salesEvent", form)
  const [salesEventDates, setSalesEventsDate] = useState(null)
  const nextTask = Form.useWatch("nextTask", form)
  const timeSlot = Form.useWatch("timeSlot", form)
  const deadline = Form.useWatch("deadLine", form)

  const { meetingPushedDeadline, disabledDeadline, endDate } = useMemo(
    () =>
      getDeadlineRules({
        taskTypes,
        leadDetailInfo,
        setFieldsValue,
        nextTask,
        subTask,
        selectedTimeSlot: timeSlot,
      }),
    [taskTypes, leadDetailInfo, nextTask, subTask, timeSlot]
  )

  useEffect(() => {
    const deadline = computeDeadline({
      subTask,
      meetingTime: timeSlot?.[0],
      salesEvent,
      taskTypes,
    })
    if (deadline && dayjs(deadline).isValid()) {
      form.setFieldValue("deadLine", deadline)
    }
  }, [subTask, timeSlot, leadDetailInfo, salesEvent])

  useEffect(() => {
    form.setFieldsValue(getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project }, isEditing))
  }, [leadDetailInfo])

  useEffect(() => {
    if (buttonText === "Back") {
      getProjects()
      getClassifications()
      getLeadDetails({
        leadId: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
        ...(isEditing === true && { detailsType: "edit_task" }),
      })
        .unwrap()
        .then(response => {
          getTaskTypes({
            params: {
              withNextStatuses: 1,
              teamType: "telesales",
              leadType: lead?.interestedIn || lead?.InternalInquiry?.interestedIn,
              ...(response?.inquiry?.MeetingPushedInstances?.[0] && {
                stage: response.inquiry.MeetingPushedInstances[0]?.CurrentStage?.key,
                saleEventStatus: !!response.inquiry.MeetingPushedInstances[0]?.InitiatorTask?.salesEventId,
              }),
            },
          })
        })
        .catch(e => notification.error({ message: e?.message }))
      getSalesEvents({ afterTime: dayjs().toISOString(), eventTypes: "sale" })
    } else if (!(buttonText === "Back")) {
      form.resetFields()
      resetErrorForAddTask()
    }
  }, [buttonText])

  const onSubmit = async values => {
    const payload = {
      lead,
      leadDetail: leadDetailInfo,
      encodedFile: values.attachment,
      ...values,
    }
    const response = isEditing
      ? await updateTask({
          ...payload,
          taskId: leadDetailInfo?.tasks?.[0]?.id,
        })
      : await addTask(payload)
    if (!response.error) {
      resetErrorForAddTask()
      setFormVisibility()
      setOpen(false)
      isEditing
        ? notification.success({
            message: `${t("Task edited against Lead ID: {{leadID}}", {
              leadID: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
            })}`,
          })
        : notification.success({
            message: `${t("Task added against Lead ID: {{leadID}}", {
              leadID: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
            })}`,
          })
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: "200",
        lead_id: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
      })
    } else {
      notification.error({ message: response?.error?.message })
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: response?.error?.status,
        lead_id: lead?.inquiryId || lead?.taskAgainstId || lead?.id,
      })
    }
  }
  const rules = schemaRules(tsrAddTaskValidationSchema())

  const taskChildren = useMemo(() => {
    return getSubTasks(taskTypes, task, leadDetailInfo, !isEditing)
  }, [task, leadDetailInfo, taskTypes, isEditing])

  const taskSubChildren = useMemo(() => {
    const subchildren = taskChildren?.subTasksMap?.[subTask]
    return filterSubTaskchildren(subchildren, salesEvents, true)
  }, [subTask, taskChildren, salesEvents])

  const subLocations = useMemo(
    () => taskSubChildren?.find(e => e?.key === subofSubTask)?.children,
    [subofSubTask, taskSubChildren]
  )

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        preserve={false}
        onFinish={onSubmit}
        initialValues={getAddTaskInitialValues({ ...leadDetailInfo, Project: lead?.Project }, isEditing)}
        style={{ paddingTop: 24 }}
      >
        {!isEditing && getAlert(leadDetailInfo)}
        <Legend title={t(isEditing ? "Edit Task" : "Add Task")} />

        <Form.Item name="task" label={t("Task")} rules={rules} className={styles.taskTypeForm} required>
          <RadioButtons
            options={taskTypes?.leadStatuses}
            onChange={(value, option) => {
              setFieldsValue({
                task: value?.target?.value,
                subTask: null,
              })
            }}
            getValue={option => option?.key}
            disabled={isEditing}
          />
        </Form.Item>
        <Form.Item
          name="completionDate"
          label={t("Completion Date")}
          required={true}
          rules={rules.concat({
            validator: async (_, value) => {
              if (value > dayjs()) {
                return Promise.reject(t("The completion date is invalid"))
              }
              return Promise.resolve()
            },
          })}
          disabled={isEditing}
        >
          <DatePicker
            range={{
              endDate: dayjs().add(0, "days"),
              startDate: dayjs()
                .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
                .startOf("day"),
            }}
            onSelect={item => {
              if (dayjs(item).isSame(dayjs(), "day")) {
                if (item > dayjs()) {
                  setFieldsValue({ completionDate: dayjs() })
                }
              }
            }}
            disableFutureTime={true}
            format={DATE_TIME_FORMAT}
            getPopupContainer={triggerNode => triggerNode.parentElement}
            disabled={isEditing}
          />
        </Form.Item>

        {!!taskChildren?.options?.length && (
          <Form.Item
            name="subTask"
            label={t("Sub Task")}
            rules={rules.concat({
              validator: async (_, value) => {
                if (value === "meeting_pushed" && !!leadDetailInfo?.isTaskInQCState && !isEditing) {
                  return Promise.reject("Cannot add task, in QC state")
                }
                return Promise.resolve()
              },
            })}
            className={styles.taskTypeForm}
            required
          >
            <RadioButtons
              options={taskChildren?.options}
              onChange={(value, option) => {
                setFieldsValue({
                  subTask: value?.target?.value,
                  subofSubTask: null,
                  nextTask: taskChildren?.options?.find(i => i?.key === value?.target?.value)?.nextStatus?.key,
                })
              }}
              disabled={isEditing}
              getValue={option => option?.key}
            />
          </Form.Item>
        )}
        {(isSubTaskMeetingPushed(subTask) || isSubTaskMeetingRescheduled(subTask)) && (
          <>
            <Card variant="gray" className="mbe-24" style={{ borderColor: theme["primary-color"] }}>
              {
                <>
                  <Form.Item label={t("Meeting Details")} className={styles.taskTypeForm}>
                    <div>{t("Please Select the date, time and location for the meeting")}</div>
                  </Form.Item>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="dateSlot" label={t("Date Slot")} required={true} rules={rules}>
                        <DatePicker
                          range={{
                            ...(!!salesEvent && {
                              endDate: dayjs(
                                salesEvents?.saleEvents?.find(item => item?.id == salesEvent)?.endTimeStamp
                              ),
                            }),
                            startDate: salesEvent
                              ? dayjs(salesEventDates?.startDate).isBefore(dayjs().startOf("day"))
                                ? dayjs().startOf("day")
                                : dayjs(salesEventDates?.startDate)
                              : dayjs()
                                  .subtract(configs?.allowBackDateForTaskCompletion ? 1 : 0, "days")
                                  .startOf("day"),
                          }}
                          format={DATE_FORMAT}
                          getPopupContainer={triggerNode => triggerNode.parentElement}
                          showTime={false}
                          onChange={date => {
                            const eventStartDate = dayjs(salesEventDates?.startDate)
                            if (salesEvent && dayjs(date).isSame(eventStartDate, "day")) {
                              const minutes = eventStartDate.minute()
                              const startDate =
                                minutes > 30
                                  ? eventStartDate.add(1, "hour").minute(0).second(0).millisecond(0)
                                  : minutes > 0
                                  ? eventStartDate.minute(30).second(0).millisecond(0)
                                  : eventStartDate
                              form.setFieldValue("timeSlot", [startDate, startDate])
                            } else {
                              form.setFieldValue("timeSlot", [date, date])
                            }
                          }}
                          disabled={isEditing}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="timeSlot" label={t("Time Slot")} required={true} rules={rules}>
                        <TimePicker
                          preSelectedDate={dateSlot}
                          minuteStep={30}
                          getPopupContainer={triggerNode => triggerNode.parentElement}
                          disabled={isEditing}
                          disableTimeRange={
                            salesEvent &&
                            (dayjs(dateSlot).isSame(dayjs(salesEventDates?.startDate), "day") ||
                              dayjs(dateSlot).isSame(dayjs(salesEventDates?.endDate), "day"))
                          }
                          timeRange={{
                            ...(dayjs(dateSlot).isSame(dayjs(salesEventDates?.startDate), "day") && {
                              startTime: dayjs(salesEventDates?.startDate),
                            }),

                            ...(dayjs(dateSlot).isSame(dayjs(salesEventDates?.endDate), "day") && {
                              endTime: dayjs(salesEventDates?.endDate),
                            }),
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              }
              {!!taskSubChildren?.length && (
                <Form.Item
                  name="subofSubTask"
                  label={t("Location")}
                  rules={rules}
                  className={styles.taskTypeForm}
                  required
                >
                  <RadioButtons options={taskSubChildren} disabled={isEditing} getValue={option => option?.key} />
                </Form.Item>
              )}
              {!!subLocations?.length && (
                <Form.Item
                  name="subLocation"
                  label={t("Sub Location")}
                  rules={rules}
                  className={styles.taskTypeForm}
                  required
                >
                  <RadioButtons options={subLocations} getValue={option => option?.key} />
                </Form.Item>
              )}
              {subofSubTask && isSubTaskSalesEvent(taskSubChildren, subofSubTask, true) && (
                <Form.Item name="salesEvent" label={t("Sales Event")} required rules={rules}>
                  <SelectInput
                    placeholder={t("Select sales event")}
                    options={salesEvents?.saleEvents}
                    inputLoading={isFetchingsSaleEvents}
                    onChange={(value, option) => {
                      const event = salesEvents?.saleEvents?.find(item => item?.id == value)
                      setSalesEventsDate({ startDate: event?.startTimeStamp, endDate: event?.endTimeStamp })
                      setFieldsValue({
                        dateSlot: null,
                        timeSlot: [],
                      })
                    }}
                  />
                </Form.Item>
              )}
              <IconText
                icon="BsInfo"
                title={t("Upon QC approval, a meeting invite will be sent to the respective user")}
                iconProps={{
                  style: { color: "#707070" },
                  hasBackground: true,
                  size: "1.35em",
                  iconContainerStyle: { padding: 0, "--icon-bg-color": "#E6E6E6" },
                }}
                style={{ alignItems: "flex-start", "--typography-color": "#9D9D9D", fontSize: 12, gap: 8 }}
              />
            </Card>
            <Form.Item name={"call"} label={t("Call Recordings")}>
              <SelectInput options={callTypes} placeholder={t("Select Call for QC")} />
            </Form.Item>
          </>
        )}
        <Form.Item name="attachment" label={t("Attachments")}>
          <UploadDragger
            name="attachment"
            files={attachment}
            setFiles={cb => {
              form.setFieldsValue({ attachment: cb(attachment) })
            }}
            disabled={isEditing}
          />
        </Form.Item>
        <Form.Item
          className={cx(!!leadDetailInfo?.suggestedComments?.length && styles.commentInput, "span-all")}
          name="comment"
          label={t("Comment")}
          rules={rules}
          required={true}
          extra={renderSuggestedComments({ suggestedComments: leadDetailInfo?.suggestedComments, setFieldsValue })}
        >
          <TextInput placeholder={t("Enter comment")} maxLength={1000} lineCount={3} />
        </Form.Item>
        <Legend title={t("Next Task")} />
        <Form.Item required name="nextTask" label={t("Next Task")} rules={rules} className={styles.taskTypeForm}>
          <RadioButtons
            options={
              isSubTaskMeetingPushed(subTask)
                ? taskTypes?.nextStatuses?.filter(item => item?.key === "meeting_confirmed")
                : taskTypes?.nextStatuses?.filter(item => item?.key !== "meeting_done")
            }
            disabled={taskTypes?.todoLogic?.[subTask]?.isTodoEnforced || subTask === "meeting_pushed"}
            getValue={option => option?.key}
          />
        </Form.Item>
        <Form.Item name="deadLine" label={t("Deadline")} required rules={rules}>
          <DatePicker
            range={{ startDate: dayjs(), endDate }}
            showTime
            disablePastTime={true}
            disabled={disabledDeadline || subTask === "meeting_pushed"}
            format={DATE_TIME_FORMAT}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
        <Form.Item name="project" label={t("Project")} rules={rules} required>
          <SelectInput
            disabled={!!leadDetailInfo?.contracts.length || isEditing}
            placeholder={t("Select project")}
            options={projects}
            getOptionValue={option => option.projectId}
            getPopupContainer={triggerNode => triggerNode.parentElement}
          />
        </Form.Item>
        <Form.Item
          name="tsrClassification"
          label={t("TSR Classification")}
          required
          rules={rules}
          className={styles.taskTypeForm}
        >
          <RadioButtons options={classifications} disabled={isEditing} />
        </Form.Item>
      </Form>

      <Row justify="end">
        <Space>
          {isError && <Alert type="error" message={error.message} />}
          <Button
            onClick={() => {
              setFormVisibility()
              resetErrorForAddTask()
            }}
            text={t("Cancel")}
            type="white"
            outlined
          />
          <Button
            loading={isLoading}
            type="primary"
            onClick={form.submit}
            text={t(isEditing ? "Update Task" : "Create Task")}
          />
        </Space>
      </Row>
    </>
  )
}

export default TsrAddTaskForm
